import { useCallback, useEffect, useState } from 'react';
import { loginOnAmazon, logoutOnAmazon } from '../../lib/amazon';
import { loginOnFacebook } from '../../lib/facebook';
import { GenericError, isGenericError } from '../../lib/error';
import { loadAmazonLoginScript, loadFacebookLoginScript } from '../../lib/loadScript';
import { useUser } from '../useUser';
import { SocialAuthProvider } from '../useSocialAuth';
import {
  CartUpdateAction,
  useUpdateCartMutation,
  CartUpdatePropertyName
} from '../../hooks/useCart';
import { PaymentSource } from '../../constants';
import { getCookies } from '../../lib/getCookies';
import { useConnectUserAccountMutation } from './useConnectUserAccountMutation';

const TOKEN_GETTERS = {
  [SocialAuthProvider.Amazon]: async () => (await loginOnAmazon()).access_token,
  [SocialAuthProvider.Facebook]: loginOnFacebook
};

export const useSocialAccountConnection = (provider: SocialAuthProvider) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<GenericError | null>(null);
  const { connectUser, disconnectUser } = useConnectUserAccountMutation();
  const { user, setUserData } = useUser();
  const { cartId } = getCookies();
  const [updateCart] = useUpdateCartMutation();

  const handleConnect = useCallback(async () => {
    try {
      const token = await TOKEN_GETTERS[provider]();
      const response = await connectUser(provider, token);
      setUserData({ ...user, ...response });
      if (provider === SocialAuthProvider.Amazon && cartId) {
        setLoading(true);
        await updateCart(CartUpdateAction.PaymentSource, {
          [CartUpdatePropertyName.PaymentSource]: PaymentSource.Amazon
        });
      }
    } catch (error) {
      if (isGenericError(error)) setError(error);
      throw error;
    }
    setLoading(false);
  }, [connectUser, cartId, provider, setUserData, updateCart, user]);

  const handleDisconnect = useCallback(async () => {
    setLoading(true);
    try {
      const response = await disconnectUser(provider);
      await setUserData({ ...user, ...response });

      if (provider === SocialAuthProvider.Amazon) {
        if (cartId) {
          await updateCart(CartUpdateAction.PaymentSource, {
            [CartUpdatePropertyName.PaymentSource]: PaymentSource.Adyen
          });
        }
        logoutOnAmazon();
      }
    } catch (error) {
      if (isGenericError(error)) setError(error);
      throw error;
    }
    setLoading(false);
  }, [disconnectUser, cartId, provider, setUserData, updateCart, user]);

  useEffect(() => {
    switch (provider) {
      case SocialAuthProvider.Amazon:
        return loadAmazonLoginScript();
      case SocialAuthProvider.Facebook:
        return loadFacebookLoginScript();
    }
  }, [provider]);

  return {
    handleConnect,
    handleDisconnect,
    loading,
    error
  };
};
